import { isSanityLink } from './links';
import { buildReferencedDocumentsQuery, CHROME_SLOT_FIELD, PAGE_MARKER } from './queries';
import type { SanityQueryOptions, SanityQueryRunner } from './query-runner';
import type { SanityPage } from './sanity-types';

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * How deep references are followed: those on a page's blocks are the first level, those inside the
 * documents they point at the second, and so on. Three is as deep as any chain goes in the
 * `development` dataset of 2026-09-30: a Belgian wind project card's project, its turbines and each
 * turbine's status (314 references at the third level, none at a fourth). A ServiceGemak product's
 * USP items are at the second. Each level costs at most one query, so this is also what keeps a page's
 * queries a fixed number.
 */
const LEVELS = 3;

/**
 * Sanity names its asset documents `image-…` and `file-…`, the format `mappers.ts#parseAssetRef` reads.
 * An image or file field holds its asset as a reference, and `toImage` builds the URL from that
 * reference itself, so it is never replaced.
 */
const isAssetId = (id: string): boolean => id.startsWith('image-') || id.startsWith('file-');

/**
 * The field naming the site a document belongs to. Every migrated document carries it (ADR-0052) and
 * the page lookup filters on it (`queries.ts`). It scopes the document rather than being content, and
 * following it would copy the site document into every entry of every list.
 */
const SITE_FIELD = 'site';

/** Sanity's own revision and timestamps: on every document, read by no component. */
const DOCUMENT_BOOKKEEPING: ReadonlySet<string> = new Set(['_rev', '_createdAt', '_updatedAt']);

/** What a reference becomes when its document does not exist, so that its field or entry goes. */
const MISSING = Symbol('missing');

/** The id of the document a reference points at, unless the reference is an asset's. */
const referencedId = (value: Record<string, unknown>): string | undefined =>
  typeof value._ref === 'string' && !isAssetId(value._ref) ? value._ref : undefined;

const isPage = (document: Record<string, unknown>): boolean => document[PAGE_MARKER] === true;

/**
 * The ids of the references in `value`, wherever they sit. A reference is told by its shape, an object
 * with a `_ref`, so a field needs no entry anywhere to be followed.
 *
 * Not a link's: the page a link points at is where it goes, not content, and all a link needs from it
 * is its address, which `links.ts` asks for on its own.
 */
const collectIds = (value: unknown, ids: Set<string>): void => {
  if (Array.isArray(value)) {
    for (const item of value) collectIds(item, ids);
    return;
  }
  if (!isObject(value) || isSanityLink(value)) return;
  const id = referencedId(value);
  if (id !== undefined) {
    ids.add(id);
    return;
  }
  for (const [key, member] of Object.entries(value)) {
    if (key !== SITE_FIELD) collectIds(member, ids);
  }
};

/**
 * The documents behind `ids`, under the id a reference holds.
 *
 * In draft mode a document can come back under its draft id, as `adapter.ts#toPage` notes for the page
 * itself, while a reference always holds the published one. So the `drafts.` prefix is dropped from
 * the key, which is all it takes for a draft to stand in for its document.
 */
const fetchDocuments = async (
  ids: ReadonlySet<string>,
  query: string,
  runner: SanityQueryRunner,
  options: SanityQueryOptions,
): Promise<Map<string, Record<string, unknown>>> => {
  const documents = new Map<string, Record<string, unknown>>();
  if (ids.size === 0) return documents;
  const result = await runner.query<unknown>(query, { ids: [...ids] }, options);
  if (!Array.isArray(result)) return documents;
  for (const document of result) {
    if (isObject(document) && typeof document._id === 'string') {
      documents.set(document._id.replace(/^drafts\./, ''), document);
    }
  }
  return documents;
};

interface Fetched {
  documents: ReadonlyMap<string, Record<string, unknown>>;
  /** Every id asked for, so that one which did not come back is known to be missing. */
  asked: ReadonlySet<string>;
  slotFields: ReadonlySet<string>;
}

/**
 * `object`'s members, each with the references in it filled in, the site left as it is, and a member
 * whose document is missing left out.
 */
const fillMembers = (
  object: Record<string, unknown>,
  level: number,
  fetched: Fetched,
  into: Record<string, unknown> = {},
): Record<string, unknown> => {
  for (const [key, member] of Object.entries(object)) {
    const filled = key === SITE_FIELD ? member : fill(member, level, fetched);
    if (filled !== MISSING) into[key] = filled;
  }
  return into;
};

/**
 * A fetched document as the content standing where its reference was: its own fields under their own
 * names, with the references inside it filled in one level further down.
 *
 * It keeps its `_id` and `_type`, which tell it apart from a block (`adapter.ts#isSlotArray`) and let
 * Visual Editing open the right document (`edit-fields.ts`), and it takes over the reference's `_key`,
 * which keeps a list entry addressable. A page keeps its own references as they are: it is where a link
 * goes, not content to show, and what a link needs from it is its address.
 */
const asContent = (
  reference: Record<string, unknown>,
  document: Record<string, unknown>,
  level: number,
  fetched: Fetched,
): Record<string, unknown> => {
  const page = isPage(document);
  const fields: Record<string, unknown> = {};
  for (const [key, member] of Object.entries(document)) {
    if (DOCUMENT_BOOKKEEPING.has(key) || key === PAGE_MARKER) continue;
    if (page && fetched.slotFields.has(key)) continue;
    fields[key] = member;
  }
  const content: Record<string, unknown> = typeof reference._key === 'string' ? { _key: reference._key } : {};
  return page ? { ...content, ...fields } : fillMembers(fields, level + 1, fetched, content);
};

/**
 * `value` with every reference in it replaced by the fields of its document.
 *
 * `level` counts the documents passed through to get here, 1 on a page's blocks. A reference deeper
 * than {@link LEVELS} was never fetched and stays as it is. A reference whose document was asked for
 * and did not come back is dropped: a single field disappears, and a list loses the entry.
 */
const fill = (value: unknown, level: number, fetched: Fetched): unknown => {
  if (Array.isArray(value)) {
    return value.map(item => fill(item, level, fetched)).filter(item => item !== MISSING);
  }
  if (!isObject(value) || isSanityLink(value)) return value;

  const id = referencedId(value);
  if (id === undefined) return fillMembers(value, level, fetched);
  if (level > LEVELS) return value;
  const document = fetched.documents.get(id);
  if (document) return asContent(value, document, level, fetched);
  return fetched.asked.has(id) ? MISSING : value;
};

/**
 * Fills in the documents a page's blocks point at, wherever the references stand and whatever their
 * fields are called.
 *
 * Sitecore hands a component a referenced item's fields (`libs/cms-sitecore/src/mappers.ts#unwrap`).
 * The migration writes the same fields as references to documents of their own, mostly site-wide
 * ones: a flow's product list, a choice step's options, a list of articles. The page query follows
 * references only under the names it lists, so everything else would reach components as a bare
 * `_ref`. This follows them by shape instead, so a new field needs no change here.
 *
 * One query per level, of at most {@link LEVELS}: first the references on the page's blocks (its own,
 * its chrome's and those inside its shared content), then those inside the documents just fetched, and
 * so on. A document is asked for once however often it is named, and a level with nothing new to ask
 * for ends it, so a page without references costs nothing. Everything runs at the page's own
 * perspective, so a draft page shows draft documents.
 */
export const createReferenceFiller = (
  runner: SanityQueryRunner,
  slotFields: readonly string[],
): ((page: SanityPage, options: SanityQueryOptions) => Promise<SanityPage>) => {
  const query = buildReferencedDocumentsQuery(slotFields);
  const blockFields = [...slotFields, CHROME_SLOT_FIELD];
  const slotFieldSet: ReadonlySet<string> = new Set(slotFields);

  return async (page, options) => {
    const documents = new Map<string, Record<string, unknown>>();
    const asked = new Set<string>();
    let pointingAt: unknown[] = blockFields.map(field => page[field]);

    for (let level = 1; level <= LEVELS; level += 1) {
      const ids = new Set<string>();
      for (const value of pointingAt) collectIds(value, ids);
      for (const id of asked) ids.delete(id);
      if (ids.size === 0) break;

      for (const id of ids) asked.add(id);
      const found = await fetchDocuments(ids, query, runner, options);
      for (const [id, document] of found) documents.set(id, document);
      pointingAt = [...found.values()].filter(document => !isPage(document));
    }
    if (asked.size === 0) return page;

    const fetched: Fetched = { documents, asked, slotFields: slotFieldSet };
    const filled: SanityPage = { ...page };
    for (const field of blockFields) {
      if (Array.isArray(page[field])) filled[field] = fill(page[field], 1, fetched);
    }
    return filled;
  };
};
